import { BIRD_EYE_API_KEY } from "@/utils";
import axios from "axios";
import { IPortfolioAssets } from "./getWalletPortfolio";

const BIRDEYE_PRICE_URL = "https://public-api.birdeye.so/defi/multi_price";
export const BIRDEYE_METADATA_URL =
  "https://public-api.birdeye.so/defi/v3/token/meta-data/multiple";

const MAX_PRICE_ADDRESSES = 100;
const MAX_METADATA_ADDRESSES = 50;

interface FetchTokenMetadataProps {
  balances: IPortfolioAssets[];
  chain?: string;
}
interface TokenPriceData {
  value: number;
  priceChange24h: number;
}

interface TokenMetadata {
  address: string;
  symbol: string;
  name: string;
  logo_uri: string;
  decimals: number;
  price: number;
  marketCap: number;
}

// Utility to split array into chunks
const chunkArray = <T>(array: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
    array.slice(index * size, index * size + size)
  );
};

const calculateValueChangeInUsd = (
  currentValue: number,
  priceChange24h: number
): number => {
  const value = Number(((priceChange24h / 100) * currentValue).toFixed(4));
  return value;
};

export const fetchTokenMetadata = async ({
  balances,
  chain = "solana",
}: FetchTokenMetadataProps): Promise<IPortfolioAssets[]> => {
  const addresses = balances.map((balance) => balance.address);
  const priceChunks = chunkArray(addresses, MAX_PRICE_ADDRESSES);
  const metadataChunks = chunkArray(addresses, MAX_METADATA_ADDRESSES);

  try {
    // Fetch metadata in chunks
    const metadataRequests = metadataChunks.map((chunk) =>
      axios.get(BIRDEYE_METADATA_URL, {
        params: { list_address: chunk.join(",") },
        headers: {
          "X-API-KEY": BIRD_EYE_API_KEY,
          accept: "application/json",
          "x-chain": chain,
        },
      })
    );

    // Fetch price data in chunks
    const priceRequests = priceChunks.map((chunk) =>
      axios.get(BIRDEYE_PRICE_URL, {
        params: { list_address: chunk.join(",") },
        headers: {
          "X-API-KEY": BIRD_EYE_API_KEY,
          accept: "application/json",
          "x-chain": chain,
        },
      })
    );
    // Resolve all API requests in parallel
    const [metadataResponses, priceResponses] = await Promise.all([
      Promise.all(metadataRequests),
      Promise.all(priceRequests),
    ]);

    // Parse metadata response
    const tokenMetadataMap: { [key: string]: TokenMetadata } = {};
    metadataResponses.forEach((response) => {
      const data = response.data.data;
      if (data) {
        Object.keys(data).forEach((key) => {
          tokenMetadataMap[key] = { address: key, ...data[key] };
        });
      }
    });

    // Parse price response
    const tokenPriceMap: { [key: string]: TokenPriceData } = {};
    priceResponses.forEach((response) => {
      const data = response.data.data;
      // console.log("Token Metadata: data", data);

      if (data) {
        Object.keys(data).forEach((key) => {
          tokenPriceMap[key] = {
            value: data[key]?.value,
            priceChange24h: data[key]?.priceChange24h,
          };
        });
      }
    });

    // Merge both metadata and price data into balances
    const updatedBalances = balances.map((balance) => {
      const metadata = tokenMetadataMap[balance.address] || {};
      const priceData = tokenPriceMap[balance.address] || {};
      const holdingInUsd = priceData.value
        ? Number((balance.uiAmount * priceData.value).toFixed(2))
        : 0;
      // const valueChangeInUsd = calculateValueChangeInUsd(
      //   priceData.value,
      //   priceData.priceChange24h || 0
      // );
      const valueChangeInUsd = priceData.priceChange24h ? (holdingInUsd * priceData.priceChange24h) / 100 : 0;

      return {
        ...balance,
        ...metadata,
        ...priceData,
        holdingInUsd,
        valueChangeInUsd,
        valueUsd: balance.uiAmount * priceData.value
      };
    });

    // // Merge both metadata and price data into balances
    // const updatedBalances = balances.map((balance) => {
    //   const priceData = tokenPriceMap[balance.address] || {};

    //   const valueChangeInUsd = calculateValueChangeInUsd(
    //     balance.priceUsd,
    //     priceData.priceChange24h || 0
    //   );

    //   return {
    //     ...balance,
    //     ...priceData,
    //     valueChangeInUsd,
    //   };
    // });

    // Sort balances in descending order of holdingInUsd
    return updatedBalances.sort(
      (a, b) => (b.valueUsd ?? 0) - (a.valueUsd ?? 0)
    );
  } catch (error) {
    console.error("Error fetching token metadata:", error);
    throw error;
  }
};
